<template>
  <hr class="separator " style="margin-top: 1px"/>

  <div>
    <div class="custom-text">
      <span class="user-manage">{{ $t('CountryManagement.title') }}</span>
    </div>
    <hr class="separator"/>
  </div>

  <div>
    <!-- SearchBar 组件 -->
    <SearchBar 
      @search="handleSearch" 
      @add="addCountry"
    />
    <!-- TableDisplay 组件 -->
    <TableDisplay 
      :tableData="tableData" 
      :loading="loading"
      @edit="handleEdit"
      @delete="handleDelete"
    />
    <!-- PaginationDisplay 组件 -->
    <div class="pagination">
      <PaginationBar :total-records="totalRecords" :current-page="currentPage" :page-size="pageSize"
                     @page-size-change="handleSizeChange" @page-change="handleCurrentChange"/>
    </div>
  </div>

  <!-- 添加/编辑国家对话框 -->
  <el-dialog
    :title="dialogTitle"
    v-model="dialogVisible"
    width="650px"
    :before-close="handleClose"
  >
    <el-form :model="countryForm" :rules="rules" ref="countryFormRef" label-width="180px">
      <el-form-item :label="$t('CountryManagement.countryName')" prop="name">
        <el-input v-model="countryForm.name" :placeholder="$t('CountryManagement.pleaseEnterCountryName')" />
      </el-form-item>
      <el-form-item :label="$t('CountryManagement.countryCode')" prop="code">
        <el-input v-model="countryForm.code" disabled :placeholder="$t('CountryManagement.pleaseEnterCountryCode')" />
      </el-form-item>
      <el-form-item :label="$t('CountryManagement.afterSaleSupport')" prop="is_after_sale">
        <el-switch
          v-model="countryForm.is_after_sale"
          :active-value="1"
          :inactive-value="0"
          :active-text="$t('CountryManagement.yes')"
          :inactive-text="$t('CountryManagement.no')"
        />
      </el-form-item>
      <el-form-item :label="$t('CountryManagement.taxNumberRule')" prop="tax_number">
        <el-input v-model="countryForm.tax_number" :placeholder="$t('CountryManagement.pleaseEnterTaxNumberRule')" />
      </el-form-item>
      <el-form-item :label="$t('CountryManagement.postcodeRule')" prop="postcode">
        <el-input v-model="countryForm.postcode" :placeholder="$t('CountryManagement.pleaseEnterPostcodeRule')" />
      </el-form-item>
    </el-form>
    <template #footer>
      <span class="dialog-footer">
        <el-button @click="handleClose">{{ $t('CountryManagement.cancel') }}</el-button>
        <el-button type="primary" @click="handleSave">{{ $t('CountryManagement.save') }}</el-button>
      </span>
    </template>
  </el-dialog>
</template>

<script>
import SearchBar from "./components/CountrySearchBar.vue";
import TableDisplay from "./components/CountryTableDisplay.vue";
import PaginationBar from "@/components/PaginationBar.vue";
import { getCountriesNew, createCountry, updateCountry, deleteCountry } from "@/api/api";
import { ElMessage, ElMessageBox } from 'element-plus';

export default {
  components: {
    SearchBar,
    TableDisplay,
    PaginationBar,
  },
  data() {
    return {
      tableData: [], // 用于存储表格数据
      totalRecords: 0, // 数据总数
      currentPage: 1, // 当前页码
      pageSize: 10, // 每页显示条数
      loading: false, // 加载状态
      isSearching: false,
      searchParams: {}, // 用于存储搜索参数
      dialogVisible: false,
      dialogTitle: '',
      isEdit: false,
      countryForm: {
        name: '',
        code: '',
        is_after_sale: 0,
        tax_number: '',
        postcode: ''
      },
      rules: {
        name: [{ required: true, message: this.$t('CountryManagement.pleaseEnterCountryName'), trigger: 'blur' }],
        code: [{ required: true, message: this.$t('CountryManagement.pleaseEnterCountryCode'), trigger: 'blur' }],
      },
      userPermissions: [], // 用户权限
    };
  },
  mounted() {
    this.loadData();
    this.loadUserPermissions();
  },
  methods: {
    // 加载用户权限
    async loadUserPermissions() {
      try {
        const userId = localStorage.getItem('userId') || sessionStorage.getItem('userId');
        const cacheKey = `userPermissions_${userId}`;
        const cache = localStorage.getItem(cacheKey) || sessionStorage.getItem(cacheKey);
        if (cache) {
          this.userPermissions = JSON.parse(cache);
        } else {
          this.userPermissions = [];
        }
      } catch (error) {
        console.error('Error accessing storage:', error);
        this.userPermissions = [];
      }
    },
    
    // 检查权限
    hasPermission(permission) {
      if (!this.userPermissions || !Array.isArray(this.userPermissions)) {
        return false;
      }
      
      const hasPermissionRecursive = (permissions) => {
        return permissions.some(perm => {
          if (perm.code === permission) {
            return true;
          }
          if (perm.children && Array.isArray(perm.children)) {
            return hasPermissionRecursive(perm.children);
          }
          return false;
        });
      };
      
      return hasPermissionRecursive(this.userPermissions);
    },
    
    async handleSearch(searchParams) {
      // 重置到第一页进行搜索
      this.currentPage = 1;
      const page = this.currentPage;
      const pageSize = this.pageSize;
      this.isSearching = true;
      this.searchParams = searchParams;
      this.loading = true;
      
      try {
        const response = await getCountriesNew(page, pageSize, this.searchParams);
        this.tableData = response.data || [];
        this.totalRecords = response.meta?.total || 0;
        this.currentPage = response.meta?.current_page || 1;
        this.pageSize = parseInt(response.meta?.per_page || pageSize);
      } catch (error) {
        ElMessage.error(this.$t('CountryManagement.searchFailed'));
      } finally {
        this.loading = false;
      }
    },

    async handleSizeChange(newSize) {
      this.pageSize = newSize;
      this.currentPage = 1;
      await this.loadData();
    },

    async handleCurrentChange(newPage) {
      this.currentPage = newPage;
      await this.loadData();
    },

    async loadData() {
      this.isSearching = true;
      this.loading = true;
      try {
        const page = this.currentPage;
        const pageSize = this.pageSize;
        // 使用当前的搜索参数加载数据
        const data = await getCountriesNew(page, pageSize, this.searchParams || {});
        if (!Array.isArray(data.data)) {
          throw new Error('Expected an array of data');
        }
        this.tableData = data.data || [];
        this.totalRecords = data.meta?.total || 0;
        this.currentPage = data.meta?.current_page || 1;
        this.pageSize = parseInt(data.meta?.per_page || pageSize, 10);
      } catch (error) {
        ElMessage.error(this.$t('CountryManagement.loadDataFailed'));
      } finally {
        this.isSearching = false;
        this.loading = false;
      }
    },

    async generateNextCode() {
      let maxNum = 0;
      let prefix = 'C';
      
      try {
        const response = await getCountriesNew(1, 1000, {});
        const allCountries = response.data || [];
        
        allCountries.forEach(row => {
          const code = row.code || '';
          const match = code.match(/^([A-Za-z]+)(\d+)$/);
          if (match) {
            const currentPrefix = match[1].toUpperCase();
            const num = parseInt(match[2], 10);
            if (!isNaN(num) && num > maxNum) {
              maxNum = num;
              prefix = currentPrefix;
            }
          }
        });
      } catch (error) {
        console.error('Failed to fetch countries for code generation:', error);
      }
      
      const nextNum = maxNum + 1;
      return prefix + nextNum.toString().padStart(3, '0');
    },

    // 添加国家
    async addCountry() {
      this.isEdit = false;
      this.dialogTitle = this.$t('CountryManagement.addCountry');
      const nextCode = await this.generateNextCode();
      this.countryForm = {
        name: '',
        code: nextCode,
        is_after_sale: 0,
        tax_number: '',
        postcode: ''
      };
      this.dialogVisible = true;
    },

    // 编辑国家
    handleEdit(row) {
      this.isEdit = true;
      this.dialogTitle = this.$t('CountryManagement.editCountry');
      this.countryForm = {
        id: row.id,
        name: row.name,
        code: row.code,
        is_after_sale: row.is_after_sale,
        tax_number: row.tax_number || '',
        postcode: row.postcode || ''
      };
      this.dialogVisible = true;
    },

    // 删除国家
    async handleDelete(row) {
      try {
        await ElMessageBox.confirm(this.$t('CountryManagement.deleteConfirm'), this.$t('CountryManagement.confirm'), {
          confirmButtonText: this.$t('CountryManagement.confirm'),
          cancelButtonText: this.$t('CountryManagement.cancel'),
          type: 'warning',
        });
        
        await deleteCountry(row.id);
        ElMessage.success(this.$t('CountryManagement.deleteSuccess'));
        this.loadData();
      } catch (error) {
        if (error !== 'cancel') {
          console.error('Delete failed:', error);
          ElMessage.error('Delete failed');
        }
      }
    },

    // 保存国家
    async handleSave() {
      try {
        await this.$refs.countryFormRef.validate();
        
        if (this.isEdit) {
          await updateCountry(this.countryForm.id, this.countryForm);
          ElMessage.success(this.$t('CountryManagement.updateSuccess'));
        } else {
          await createCountry(this.countryForm);
          ElMessage.success(this.$t('CountryManagement.addSuccess'));
        }
        
        this.dialogVisible = false;
        this.loadData();
      } catch (error) {
        console.error('Save failed:', error);
        ElMessage.error('Save failed');
      }
    },

    // 关闭对话框
    handleClose() {
      this.dialogVisible = false;
      this.$refs.countryFormRef.resetFields();
    }
  },
};
</script>

<style scoped>
@import "@/assets/css/publicStyle.css";

.custom-text {
  margin-left: 15px;
}

.pagination {
  margin-top: 20px;
  text-align: center;
}

.dialog-footer {
  text-align: right;
}

/* 防止布局拉伸 - 使用更稳定的布局 */
.search-container {
  height: 120px;
  min-height: 120px;
  max-height: 120px;
  overflow: hidden;
}

.table-container {
  min-height: 400px;
  height: auto;
  overflow: hidden;
}

/* 确保表格列宽固定 */
.el-table {
  table-layout: fixed;
}

/* 防止整个页面布局变化 */
:deep(.el-main) {
  overflow-x: hidden;
}

/* 强制稳定布局 */
* {
  box-sizing: border-box;
}
</style> 